import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import client from '@yesplz/client';
import Widget from '../modules/Widget';
import noUiSlider from 'nouislider';
import { formatCurrency, findLabelByLng } from '@yesplz/core';

const { document } = window;

const TEMPLATE = `
<div class="yesplz-price-range-container">
  <div id="yesplz-price-range-title" class="yesplz-price-range-title hidden"></div>
  <div id="yesplz-price-range-slider" class="yesplz-price-range-slider"></div>
  <div class="yesplz-price-range-values">
    <span id="yesplz-price-range-value-low"></span>
    <span id="yesplz-price-range-value-high"></span>
  </div>
</div>
`;

export class PriceRangeFilter extends Widget {
  defaultParams = {
    title: 'Price',
    step: null,
    overMessage: [{ lng: 'en', label: 'Over {price}' }, { lng: 'ko', label: '{price} 이상' }],
    currency: {},
    filterBy: 'resultFilters',
  };

  constructor(params) {
    super(params);

    const element = document.createElement('div');
    element.className = this.params.containerClassName || 'yesplz-price-range-filter';
    this.mainElement = element;

    this.allPrices = {};

    this.mainElement.innerHTML = TEMPLATE;

    this.slider = null;

    this.params.filterByOriginal = this.params.filterBy;
    this.params.filterBy =  ['brands'].includes(this.params.filterBy) ? `params.${this.params.filterBy}` : this.params.filterBy;
  }

  didMount() {
    if (this.params.filterBy !== 'resultFilters')
      this.fetchAndRenderPrices();
  }

  didUpdate(prevState) {
    if (
      this.params.filterBy === 'resultFilters'
      &&
      !isEqual(
        prevState.search?.filters?.salePrice,
        this.state.search?.filters?.salePrice
      )
    ) {
      this.renderPrices();
    }
    else if (this.params.filterBy !== 'resultFilters' && !isEqual(
      get(prevState.filter, this.params.filterBy),
      get(this.state.filter, this.params.filterBy)
    )) {
      this.fetchAndRenderPrices();
    }
    else if (
      !isEqual(prevState.filter.params.price, this.state.filter.params.price)
    ) {
      this.renderLabels(...this.values);
      if (this.slider.noUiSlider) {
        const [low, high] = this.values;
        this.slider.noUiSlider.updateOptions({
          start: [low || this.range.min, high || this.range.max],
        });
      }
    }
  }

  formatCurrency(number) {
    let currency = this.params.currency;
    if (this.prices?.currency) {
      currency = {
        code: this.prices.currency,
      };
    }
    return formatCurrency(number, currency);
  }

  get prices() {
    if (this.params.filterBy === 'resultFilters') {
      return this.state.search?.filters?.salePrice || null;
    }
    const id = this.indexId;
    return this.allPrices[id];
  }

  get range() {
    if ((this.prices.minPrice === 0 || this.prices.minPrice) && (this.prices.maxPrice === 0 || this.prices.maxPrice)) {
      const range = {
        min: Math.floor(this.prices.minPrice / this.step) * this.step,
        max: Math.ceil(
          (this.prices.overPrice && this.prices.overPrice < this.prices.maxPrice ? this.prices.overPrice : this.prices.maxPrice) / this.step
        ) * this.step,
      };
      if (range.min === range.max) {
        range.min = 0
      }
      return range;
    }
    else {
      const prices = Object.keys(this.prices);
      const min = parseInt(prices[0].replace('$', ''));
      const max = parseInt(prices[prices.length - 1].replace('$', ''));
      return { min, max };
    }
  }

  get step() {
    if (this.params.step) {
      return this.params.step;
    }

    if (this.prices.step) {
      return this.prices.step;
    }

    const prices = Object.values(this.prices);
    if (prices[0].includes('-'))
      return parseInt(prices[0].split('-')[1]);
    else
      return prices[1] - prices[0];
  }

  get values() {
    const price = this.state.filter.params.price;

    if (!price) return [];

    const [low, high] = price.split('-');

    return [low, high || this.range.max];
  }

  handlePriceChange = (low, high) => {
    const { params } = this.state.filter;
    this.setFilter({
      params: {
        ...params,
        price: `${low}-${high < this.range.max ? high : ''}`,
      },
    });
  }

  handlePriceUpdate = (low, high) => {
    this.renderLabels(low, high);
  }

  get indexId() {
    const { filterBy } = this.params;
    const value = get(this.state.filter, filterBy);
    return Array.isArray(value) ? value.join(',') : value;
  }

  async fetchAndRenderPrices() {
    const { filterBy } = this.params;
    const id = this.indexId;
    const { searchAdditionalParams } = this.state.config;
    if (!this.allPrices[id]) {
      this.allPrices[id] = await (() => {
        if (filterBy === 'categoryId') {
          return client.pricesByCategories(id, searchAdditionalParams);
        }
        else {
          return client.priceFilters({
            [this.params.filterByOriginal]: id,
          }, searchAdditionalParams);
        }
      })();
    }
    this.renderPrices();
  }

  renderLabels(low, high) {
    const { lng } = this.state.config;
    const lowElement = document.getElementById('yesplz-price-range-value-low');
    const highElement = document.getElementById('yesplz-price-range-value-high');
    const lowText = `${this.formatCurrency(low || this.range.min || 1)}`;
    const highText = (
      high && high !== this.range.max
        ? `${this.formatCurrency(high)}`
        : findLabelByLng(this.params.overMessage, lng)
            .replace('{price}', this.formatCurrency(this.range.max || 100))
    );

    lowElement.innerHTML = lowText;
    highElement.innerHTML = highText;

    const title = document.getElementById('yesplz-price-range-title');
    title.classList.remove('hidden');
    title.innerHTML = `(${lowText} ~ ${highText})`;

    if (typeof this.params.onUpdated === 'function')
      this.params.onUpdated(low, high);
  }

  renderPrices() {
    if (!this.prices) return;

    const slider = document.getElementById('yesplz-price-range-slider');
    
    if (!slider) return;
    
    const [low, high] = this.values;

    if (slider.noUiSlider) {
      slider.noUiSlider.destroy();
    }

    noUiSlider.create(slider, {
      start: [low || this.range.min, high || this.range.max || this.step],
      step: this.step,
      connect: true,
      margin: this.step,
      range: {
        ...this.range,
        max: this.range.max === this.range.min ? this.range.max + this.step : this.range.max,
      },
    });

    // slider.setAttribute('disabled', true);

    slider.noUiSlider.on('change', ([lowStr, highStr]) => {
      this.handlePriceChange(parseInt(lowStr), parseInt(highStr));
    });

    slider.noUiSlider.on('update', ([lowStr, highStr]) => {
      this.handlePriceUpdate(parseInt(lowStr), parseInt(highStr));
    });

    this.renderLabels(...this.values);

    this.slider = slider;

    if (typeof this.params.onRendered === 'function')
      this.params.onRendered(this.range);
  }

  render() {
    const title = document.createElement('h3');
    title.innerText = this.params.title;
    this.container.appendChild(title);
    return this.mainElement;
  }
}

export default (params) => {
  return new PriceRangeFilter(params);
};
